import React from "react";
import { motion } from "framer-motion";
import { X, Check, Eye, PackageOpen, FileText, Home, Clock, IndianRupee } from "lucide-react";
import { classes } from "./tokens";

/**
 * Transparency section — the core differentiator vs local garages.
 *
 * Structure:
 *  [Left]  Big hero image (reused doorstep service photo)
 *  [Right] 4 transparency guarantee rows + side-by-side Old vs Wisedrive table
 *
 * This addresses the user's explicit ask:
 *   "if car is given in a garage you dont know which parts they use ...
 *    with wisedrive you get to see everything in front of you ...
 *    100% transparency and no cheating ... we open the engine oil seal
 *    in front of you and change the spare parts right in front of the
 *    customer ... doorstep and at your convenient time"
 */
const guarantees = [
  {
    icon: PackageOpen,
    title: "Spare parts opened in front of you",
    desc: "Every OEM box, every oil seal is broken open right at your doorstep — not behind a closed workshop door.",
  },
  {
    icon: Eye,
    title: "Watch the whole service, live",
    desc: "You see every step — engine oil drain, filter swap, inspection. No ‘come back in 4 hours’.",
  },
  {
    icon: FileText,
    title: "No surprise estimates",
    desc: "Locked-in price before we start. Anything extra needs your explicit OK — with photo evidence, no pressure.",
  },
  {
    icon: Home,
    title: "At your home, on your schedule",
    desc: "Parking lot, office basement, or porch — we come to you. Service done in 60 minutes while you get on with your day.",
  },
];

const compareRows = [
  {
    label: "Who's watching?",
    old: { text: "Car dropped off. You leave. No visibility.", bad: true },
    wd:  { text: "You watch every step at your doorstep.", bad: false },
  },
  {
    label: "Spare parts",
    old: { text: "Swapped behind closed doors. Are they genuine?", bad: true },
    wd:  { text: "OEM box opened in front of you. Sealed bottles, fresh filters.", bad: false },
  },
  {
    label: "Final bill",
    old: { text: "“Sir, extra work tha — ₹2,000 extra”.", bad: true },
    wd:  { text: "Locked price. Any extras need YOUR photo-based approval.", bad: false },
  },
  {
    label: "Your time",
    old: { text: "Drop-off + pick-up + waiting = half your day gone.", bad: true },
    wd:  { text: "60 minutes at your doorstep. Keep working, keep living.", bad: false },
  },
  {
    label: "Warranty",
    old: { text: "“Warranty?” … *shrug*.", bad: true },
    wd:  { text: "1,000 km / 30-day warranty in writing.", bad: false },
  },
];

const CarServiceTransparency = () => (
  <section
    className="py-16 lg:py-24 bg-slate-50 border-y border-slate-100"
    data-testid="transparency-section"
  >
    <div className={classes.container}>
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
        <span className={classes.overline}>The Wisedrive Difference</span>
        <h2 className="text-3xl sm:text-4xl lg:text-[52px] tracking-tight font-extrabold text-slate-900 leading-[1.15] lg:leading-[1.1] mt-4">
          <span className="block">Zero surprises. <span className="text-blue-600">Zero cheating.</span></span>
          <span className="block text-slate-500 mt-2 lg:mt-3">Everything happens right in front of you.</span>
        </h2>
        <p className="mt-6 lg:mt-7 text-base lg:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          Every garage horror story starts with <strong className="text-slate-900">"I couldn't see what they did."</strong> Not with Wisedrive. Our certified mechanic comes to your doorstep, opens every spare part box in front of you, and changes everything while you watch. Convenient for you. Honest by design.
        </p>
      </div>

      {/* Guarantees grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-14 lg:mb-20">
        {guarantees.map((g, i) => (
          <motion.div
            key={g.title}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.35, delay: i * 0.06 }}
            className="bg-white rounded-2xl p-5 lg:p-6 border border-slate-200 hover:border-blue-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all"
            data-testid={`transparency-card-${i}`}
          >
            <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 inline-flex items-center justify-center mb-4">
              <g.icon className="h-5 w-5 text-blue-600" strokeWidth={2.2} />
            </div>
            <div className="text-base font-extrabold text-slate-900 leading-tight mb-2">
              {g.title}
            </div>
            <p className="text-sm text-slate-500 leading-relaxed">{g.desc}</p>
          </motion.div>
        ))}
      </div>

      {/* Side-by-side comparison */}
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <span className={classes.overline}>Side by Side</span>
          <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2">
            Local Garage <span className="text-slate-400">vs</span> Wisedrive
          </h3>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          {/* Table header */}
          <div className="grid grid-cols-7 bg-slate-900 text-white">
            <div className="col-span-3 sm:col-span-3 px-4 sm:px-6 py-4 text-[11px] font-extrabold uppercase tracking-[0.18em] text-slate-400">
              Experience
            </div>
            <div className="col-span-2 sm:col-span-2 px-3 sm:px-6 py-4 text-[11px] font-extrabold uppercase tracking-[0.18em] text-slate-400 text-center border-l border-slate-800">
              Local Garage
            </div>
            <div className="col-span-2 sm:col-span-2 px-3 sm:px-6 py-4 text-[11px] font-extrabold uppercase tracking-[0.18em] text-blue-400 text-center bg-gradient-to-b from-blue-600/20 to-transparent border-l border-slate-800">
              Wisedrive
            </div>
          </div>

          {/* Rows */}
          {compareRows.map((r, i) => (
            <motion.div
              key={r.label}
              initial={{ opacity: 0, x: -8 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="grid grid-cols-7 border-t border-slate-100 hover:bg-slate-50/50 transition-colors"
            >
              <div className="col-span-3 sm:col-span-3 px-4 sm:px-6 py-4 text-sm font-bold text-slate-900 flex items-center">
                {r.label}
              </div>
              <div className="col-span-2 sm:col-span-2 px-3 sm:px-6 py-4 text-xs sm:text-sm text-slate-500 border-l border-slate-100 flex items-start gap-2">
                <X className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
                <span>{r.old.text}</span>
              </div>
              <div className="col-span-2 sm:col-span-2 px-3 sm:px-6 py-4 text-xs sm:text-sm text-slate-700 font-semibold bg-blue-50/30 border-l border-slate-100 flex items-start gap-2">
                <Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <span>{r.wd.text}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer convenience pills */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <ConveniencePill icon={Home} text="At your doorstep" />
          <ConveniencePill icon={Clock} text="At your convenient time" />
          <ConveniencePill icon={IndianRupee} text="At a locked price" />
        </div>
      </div>
    </div>
  </section>
);

const ConveniencePill = ({ icon: Icon, text }) => (
  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm">
    <Icon className="h-4 w-4 text-blue-600" strokeWidth={2.4} />
    <span className="text-sm font-extrabold text-slate-900 tracking-tight">{text}</span>
  </div>
);

export default CarServiceTransparency;
